import React, { useEffect, useState } from 'react';
import { Star, Loader2, MessageSquareHeart, ShieldOff } from 'lucide-react';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const StarPicker = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-1" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" onClick={() => onChange(n)} data-testid={`${testid}-${n}`} className="p-0.5">
        <Star className="w-7 h-7 transition-colors" fill={n <= value ? '#A44758' : 'none'} stroke="#A44758" />
      </button>
    ))}
  </div>
);

const ShareExperience = () => {
  const { toast } = useToast();
  const [programs, setPrograms] = useState([]);
  const [mine, setMine] = useState([]);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [programId, setProgramId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [fbText, setFbText] = useState('');
  const [fbRating, setFbRating] = useState(0);
  const [fbAnon, setFbAnon] = useState(false);
  const [fbSubmitting, setFbSubmitting] = useState(false);

  const load = async () => {
    try {
      const [b, m] = await Promise.all([api.get('/bookings/mine'), api.get('/testimonials/mine')]);
      const seen = {};
      (b.data || []).filter((x) => x.status === 'confirmed').forEach((x) => { seen[x.program_id] = x.program_title; });
      setPrograms(Object.entries(seen).map(([id, title]) => ({ id, title })));
      setMine(m.data || []);
    } catch (e) { console.error('Failed to load:', e); }
  };
  useEffect(() => { load(); }, []);

  const submitTestimonial = async (e) => {
    e.preventDefault();
    if (text.trim().length < 10) { toast({ title: 'Please write a little more (min 10 characters).' }); return; }
    setSubmitting(true);
    try {
      const prog = programs.find((p) => p.id === programId);
      await api.post('/testimonials', { rating, text, program_id: programId || null, program_title: prog?.title || null });
      toast({ title: 'Thank you!', description: 'Your testimonial is awaiting review.' });
      setText(''); setRating(5); setProgramId('');
      load();
    } catch (err) { toast({ title: 'Could not submit', description: err?.response?.data?.detail, variant: 'destructive' }); }
    finally { setSubmitting(false); }
  };

  const submitFeedback = async (e) => {
    e.preventDefault();
    if (fbText.trim().length < 5) { toast({ title: 'Please share a bit more.' }); return; }
    setFbSubmitting(true);
    try {
      await api.post('/feedback', { text: fbText, rating: fbRating || null, is_anonymous: fbAnon });
      toast({ title: 'Feedback sent', description: fbAnon ? 'Submitted anonymously.' : 'Thank you!' });
      setFbText(''); setFbRating(0); setFbAnon(false);
    } catch (err) { toast({ title: 'Could not send', description: err?.response?.data?.detail }); }
    finally { setFbSubmitting(false); }
  };

  const statusStyle = { published: 'bg-green-100 text-green-800', pending: 'bg-amber-100 text-amber-800', rejected: 'bg-red-100 text-red-800' };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="share-experience-page">
      <div>
        <div className="eyebrow mb-2">Your voice</div>
        <h1 className="font-serif-display text-4xl text-burgundy">Share Your Experience</h1>
      </div>

      {/* Testimonial */}
      <form onSubmit={submitTestimonial} className="p-6 rounded-2xl border border-rose/50 bg-white/70 space-y-4" data-testid="testimonial-form">
        <div className="flex items-center gap-2 text-burgundy font-medium"><MessageSquareHeart className="w-5 h-5" /> Write a public testimonial</div>
        <p className="text-sm text-mahogany/70">Shared with your name after our team reviews it. Only available once you've attended a program.</p>
        <div>
          <label className="block text-xs uppercase tracking-widest text-mahogany/70 mb-1.5">Your rating</label>
          <StarPicker value={rating} onChange={setRating} testid="testimonial-rating" />
        </div>
        {programs.length > 0 && (
          <div>
            <label className="block text-xs uppercase tracking-widest text-mahogany/70 mb-1.5">Which program? (optional)</label>
            <select value={programId} onChange={(e) => setProgramId(e.target.value)} data-testid="testimonial-program"
              className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none">
              <option value="">Select a program…</option>
              {programs.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </div>
        )}
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Share how your experience touched you…"
          data-testid="testimonial-text" className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany" />
        <button type="submit" disabled={submitting} data-testid="testimonial-submit" className="pill-btn">
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Submit testimonial
        </button>
      </form>

      {/* My submissions */}
      {mine.length > 0 && (
        <div className="space-y-2" data-testid="my-testimonials">
          <h2 className="text-lg text-burgundy">Your testimonials</h2>
          {mine.map((t) => (
            <div key={t.id} className="p-4 rounded-xl border border-rose/40 bg-white/60 flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-1 mb-1">{Array.from({ length: t.rating }).map((_, s) => <Star key={s} className="w-3.5 h-3.5" fill="#A44758" stroke="#A44758" />)}</div>
                <p className="text-sm text-burgundy/90">"{t.text}"</p>
              </div>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full shrink-0 ${statusStyle[t.status] || ''}`}>{t.status}</span>
            </div>
          ))}
        </div>
      )}

      {/* Feedback */}
      <form onSubmit={submitFeedback} className="p-6 rounded-2xl border border-rose/50 bg-cream-2/40 space-y-4" data-testid="feedback-form">
        <div className="flex items-center gap-2 text-burgundy font-medium"><ShieldOff className="w-5 h-5" /> Private feedback</div>
        <p className="text-sm text-mahogany/70">Goes straight to our team — not published. You can send it anonymously.</p>
        <div>
          <label className="block text-xs uppercase tracking-widest text-mahogany/70 mb-1.5">Rating (optional)</label>
          <StarPicker value={fbRating} onChange={setFbRating} testid="feedback-rating" />
        </div>
        <textarea value={fbText} onChange={(e) => setFbText(e.target.value)} rows={3} placeholder="Tell us how we can improve…"
          data-testid="feedback-text" className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany" />
        <label className="flex items-center gap-2 text-sm text-mahogany/80">
          <input type="checkbox" checked={fbAnon} onChange={(e) => setFbAnon(e.target.checked)} data-testid="feedback-anonymous" />
          Send anonymously (we won't see your name)
        </label>
        <button type="submit" disabled={fbSubmitting} data-testid="feedback-submit" className="pill-btn-outline">
          {fbSubmitting && <Loader2 className="w-4 h-4 animate-spin" />} Send feedback
        </button>
      </form>
    </div>
  );
};

export default ShareExperience;
